/**
 * Category Section Component
 * Collapsible section for grouped documents
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ChevronDown, ChevronRight, FileText, Banknote, Plane, Receipt, FileCheck, Folder } from 'lucide-react';
import DocumentCard from './DocumentCard';
import DocumentRow from './DocumentRow';

const CATEGORY_ICONS = {
  tax_documents: FileText,
  pay_earnings: Banknote,
  travel: Plane,
  receipts: Receipt,
  contracts: FileCheck,
  other: Folder
};

const CategorySection = ({ category, viewMode, onEdit, onDelete, onPreview, onLink, onSavePassword }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const Icon = CATEGORY_ICONS[category.key] || Folder;

  return (
    <Card className="bg-slate-800 border-slate-700 overflow-hidden" data-testid={`category-section-${category.key}`}>
      {/* Category Header */}
      <CardHeader 
        className={`py-3 px-4 cursor-pointer hover:bg-slate-700/50 transition-colors ${category.bgColor}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${category.bgColor} ${category.borderColor} border`}>
              <Icon className={`h-5 w-5 ${category.textColor}`} />
            </div>
            <div>
              <h3 className={`font-semibold ${category.textColor}`}>
                {category.label}
              </h3>
              <p className="text-xs text-slate-500">
                {category.documents.length} document{category.documents.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
            {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </Button>
        </div>
      </CardHeader>

      {/* Documents */}
      {isExpanded && (
        <CardContent className="p-4 pt-0">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {category.documents.map((doc) => (
                <DocumentCard 
                  key={doc.id} 
                  doc={doc} 
                  onEdit={onEdit} 
                  onDelete={onDelete}
                  onPreview={onPreview}
                  onLink={onLink}
                  onSavePassword={onSavePassword}
                  categoryColor={category.textColor}
                />
              ))}
            </div>
          ) : (
            <div className="divide-y divide-slate-700 mt-4 border border-slate-700 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-slate-900/50 text-xs font-medium text-slate-400 uppercase tracking-wider">
                <div className="col-span-4">File Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Tax Year</div>
                <div className="col-span-2">Uploaded</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {category.documents.map((doc) => (
                <DocumentRow 
                  key={doc.id} 
                  doc={doc} 
                  onEdit={onEdit} 
                  onDelete={onDelete}
                  onPreview={onPreview}
                  onLink={onLink}
                  onSavePassword={onSavePassword}
                  categoryColor={category.textColor}
                />
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default CategorySection;
